"use client";

import {
  AlertTriangle,
  BarChart3,
  Bot,
  Briefcase,
  Building2,
  CalendarCheck,
  CircleHelp,
  Cog,
  FileText,
  Grid3x3,
  Hammer,
  History,
  Info,
  Layers,
  LayoutGrid,
  ListOrdered,
  Mail,
  Newspaper,
  Package,
  Palette,
  Search,
  Send,
  Sparkles,
  Sprout,
  Star,
  ThumbsUp,
  Users,
  Zap,
  type LucideIcon,
} from "lucide-react";
import {
  Fragment,
  useEffect,
  useRef,
  useState,
  type ElementType,
  type ReactNode,
} from "react";

import type { CcEyebrowIconName } from "@/components/ui/cc-eyebrow-icon-name";

const CC_EYEBROW_ICONS: Record<CcEyebrowIconName, LucideIcon> = {
  AlertTriangle,
  BarChart3,
  Bot,
  Briefcase,
  Building2,
  CalendarCheck,
  CircleHelp,
  Cog,
  FileText,
  Grid3x3,
  Hammer,
  History,
  Info,
  Layers,
  LayoutGrid,
  ListOrdered,
  Mail,
  Newspaper,
  Package,
  Palette,
  Search,
  Send,
  Sparkles,
  Sprout,
  Star,
  ThumbsUp,
  Users,
  Zap,
};

/** Ratio thresholds fail for short labels under fixed nav or tall sections (see CcProductCanvas). */
const ENTER_THRESHOLD = 0;
const ENTER_ROOT_MARGIN = "0px 0px -12% 0px";

const SANJAYA_SCRAMBLE_CHARACTERS = "@#$%^&*()_+[]{}|;:,.<>?~";
const SANJAYA_SCRAMBLE_DELAY_MS = 500;
const SANJAYA_SCRAMBLE_LETTERS = 10;
// Framer maps its speed value of 92 to an 18ms tick.
const SANJAYA_SCRAMBLE_TICK_MS = 18;

const NEIDEN_CHEVRON_OFFSETS = [0, 4.25, 8.5, 12.75, 17] as const;
const NEIDEN_CHEVRON_WIDTH = 5.5;

function CcEyebrowPowderMark() {
  return <span className="cc-eyebrow__powder-dot" />;
}

function CcEyebrowNeidenMark() {
  const lastTip =
    NEIDEN_CHEVRON_OFFSETS[NEIDEN_CHEVRON_OFFSETS.length - 1] + NEIDEN_CHEVRON_WIDTH;

  return (
    <svg
      className="cc-eyebrow__icon cc-eyebrow__icon--neiden"
      viewBox={`0 0 ${lastTip} 16`}
      width={Math.round(lastTip)}
      height={16}
      aria-hidden="true"
      focusable="false"
    >
      {NEIDEN_CHEVRON_OFFSETS.map((x, index) => (
        <path
          key={x}
          d={`M${x} 0.5 L${x} 15.5 L${x + NEIDEN_CHEVRON_WIDTH} 8 Z`}
          fill="currentColor"
          opacity={1 - index * 0.18}
        />
      ))}
    </svg>
  );
}

export type CcEyebrowTheme = "default" | "on-subtle" | "on-dark";

type CcEyebrowProps = {
  children: ReactNode;
  className?: string;
  as?: ElementType;
  /** `on-subtle` for light gray section bands (`$cc-color-bg-hero`); `on-dark` for dark surfaces. */
  theme?: CcEyebrowTheme;
  /** First N revealed characters styled as accent (e.g. hero "New" prefix). */
  accentCharCount?: number;
  /** Lucide icon key (resolved in this client component; safe from Server Components). */
  iconName?: CcEyebrowIconName;
  /** Rendered inside the pill before label copy (e.g. custom badge). */
  prefix?: ReactNode;
  /** Rendered inside the pill after label copy (e.g. chevron). */
  suffix?: ReactNode;
  /** With `cc-eyebrow--neiden`, label before the em dash (default CC). */
  neidenBrand?: string;
  /** Per-character glyphs with Sanjaya stagger (`CcSanjayaEyebrow`). */
  sanjayaGlyphs?: boolean;
};

function flattenText(children: ReactNode): string {
  if (typeof children === "string" || typeof children === "number") {
    return String(children);
  }

  if (Array.isArray(children)) {
    return children.map(flattenText).join("");
  }

  return "";
}

function renderCopy(
  text: string,
  options?: {
    accentCharCount?: number;
    sanjayaGlyphs?: boolean;
    scrambleProgress?: number;
    scrambledText?: string;
  },
) {
  const accentCharCount = options?.accentCharCount;
  const sanjayaGlyphs = options?.sanjayaGlyphs;

  if (!accentCharCount && !sanjayaGlyphs) {
    return text;
  }

  const progress = options?.scrambleProgress ?? 1;
  const hiddenEnd = Math.max(
    0,
    Math.min(text.length, Math.trunc(text.length + progress * (-SANJAYA_SCRAMBLE_LETTERS - text.length))),
  );
  const scrambledEnd = Math.max(
    0,
    Math.min(text.length, Math.trunc(text.length + SANJAYA_SCRAMBLE_LETTERS - progress * (text.length + SANJAYA_SCRAMBLE_LETTERS))),
  );

  return [...text].map((char, index) => {
    if (/\s/.test(char)) {
      return <Fragment key={`space-${index}`}>{char}</Fragment>;
    }

    const isHidden = sanjayaGlyphs && index < hiddenEnd;
    const isScrambled = sanjayaGlyphs && index >= hiddenEnd && index < scrambledEnd;

    return (
      <span
        key={`${char}-${index}`}
        className={[
          "cc-eyebrow__glyph",
          isHidden ? "cc-eyebrow__glyph--hidden" : "",
          isScrambled ? "cc-eyebrow__glyph--scrambled" : "cc-eyebrow__glyph--done",
          accentCharCount && index < accentCharCount ? "cc-eyebrow__glyph--accent" : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {isScrambled ? options?.scrambledText?.[index] ?? char : char}
      </span>
    );
  });
}

function makeScrambledText(text: string) {
  let previous = "";

  return [...text]
    .map((char) => {
      let next = previous;

      while (next === previous) {
        next =
          SANJAYA_SCRAMBLE_CHARACTERS[
            Math.floor(Math.random() * SANJAYA_SCRAMBLE_CHARACTERS.length)
          ] ?? "#";
      }

      previous = next;
      return char === char.toUpperCase() ? next.toUpperCase() : next.toLowerCase();
    })
    .join("");
}

export function CcEyebrow({
  children,
  className,
  as: Tag = "span",
  theme = "default",
  accentCharCount,
  iconName,
  prefix,
  suffix,
  neidenBrand = "CC",
  sanjayaGlyphs = false,
}: CcEyebrowProps) {
  const isNeiden = className?.includes("cc-eyebrow--neiden") ?? false;
  const isPowder = className?.includes("cc-eyebrow--powder") ?? false;
  const Icon =
    !isNeiden && !isPowder && iconName ? CC_EYEBROW_ICONS[iconName] : undefined;
  const rootRef = useRef<HTMLElement | null>(null);
  const text = flattenText(children);
  const prefixText = prefix ? flattenText(prefix) : "";
  const neidenLabel = isNeiden ? `[ ${neidenBrand} — ${text} ]` : "";
  const accessibleName = isNeiden
    ? neidenLabel
    : [prefixText, text].filter(Boolean).join(": ");
  const [entered, setEntered] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [scrambleProgress, setScrambleProgress] = useState(0);
  const [scrambledText, setScrambledText] = useState(() => makeScrambledText(text));

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReducedMotion(media.matches);
    sync();
    media.addEventListener("change", sync);
    return () => media.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    const node = rootRef.current;
    if (!node) {
      return;
    }

    if (reducedMotion) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) {
          return;
        }

        setEntered(true);
        observer.disconnect();
      },
      { threshold: ENTER_THRESHOLD, rootMargin: ENTER_ROOT_MARGIN },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [reducedMotion]);

  useEffect(() => {
    if (!sanjayaGlyphs || !entered || reducedMotion) {
      return;
    }

    let frame = 0;
    let scrambleTimer = 0;
    const duration = SANJAYA_SCRAMBLE_TICK_MS * (text.length + SANJAYA_SCRAMBLE_LETTERS);

    const delayTimer = window.setTimeout(() => {
      const startedAt = performance.now();

      scrambleTimer = window.setInterval(() => {
        setScrambledText(makeScrambledText(text));
      }, SANJAYA_SCRAMBLE_TICK_MS);

      const animate = (now: number) => {
        const progress = Math.min(1, (now - startedAt) / duration);
        setScrambleProgress(progress);

        if (progress < 1) {
          frame = window.requestAnimationFrame(animate);
        } else {
          window.clearInterval(scrambleTimer);
        }
      };

      frame = window.requestAnimationFrame(animate);
    }, SANJAYA_SCRAMBLE_DELAY_MS);

    return () => {
      window.clearTimeout(delayTimer);
      window.clearInterval(scrambleTimer);
      window.cancelAnimationFrame(frame);
    };
  }, [entered, reducedMotion, sanjayaGlyphs, text]);

  const themeClass =
    theme === "on-subtle"
      ? "cc-eyebrow--on-subtle"
      : theme === "on-dark"
        ? "cc-eyebrow--on-dark"
        : "";

  const classes = ["cc-eyebrow", "cc-eyebrow--pill", themeClass, className]
    .filter(Boolean)
    .join(" ");

  const enterState = reducedMotion || entered ? "end" : "start";

  return (
    <Tag
      ref={rootRef}
      className={classes}
      data-enter={enterState}
    >
      <span className="cc-eyebrow__accessible-copy">{accessibleName || text}</span>
      <span className="cc-eyebrow__bg" aria-hidden="true" />
      {isPowder ? (
        <span className="cc-eyebrow__icon-wrap cc-eyebrow__icon-wrap--powder" aria-hidden="true">
          <CcEyebrowPowderMark />
        </span>
      ) : isNeiden ? (
        <span className="cc-eyebrow__icon-wrap cc-eyebrow__icon-wrap--neiden" aria-hidden="true">
          <CcEyebrowNeidenMark />
        </span>
      ) : Icon ? (
        <span className="cc-eyebrow__icon-wrap" aria-hidden="true">
          <Icon className="cc-eyebrow__icon" size={14} strokeWidth={2} />
        </span>
      ) : null}
      {prefix ? (
        <span className="cc-eyebrow__prefix" aria-hidden="true">
          {prefix}
        </span>
      ) : null}
      <span className="cc-eyebrow__copy" aria-hidden="true">
        {isNeiden ? (
          <>
            <span className="cc-eyebrow__neiden-brand">{neidenBrand}</span>
            <span className="cc-eyebrow__neiden-dash"> — </span>
            {renderCopy(text, {
              accentCharCount,
              sanjayaGlyphs,
              scrambleProgress: reducedMotion ? 1 : scrambleProgress,
              scrambledText,
            })}
          </>
        ) : (
          renderCopy(text, {
            accentCharCount,
            sanjayaGlyphs,
            scrambleProgress: reducedMotion ? 1 : scrambleProgress,
            scrambledText,
          })
        )}
      </span>
      {suffix ? (
        <span className="cc-eyebrow__suffix" aria-hidden="true">
          {suffix}
        </span>
      ) : null}
      {!isNeiden && !isPowder ? (
        <span className="cc-eyebrow__divider" aria-hidden="true" />
      ) : null}
    </Tag>
  );
}
